import Navbar from "@/components/Navbar";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { ArrowLeft, FileText, Download } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

const AnalysisTools = () => {
  const navigate = useNavigate();
  
  const handleViewDetails = (title: string) => {
    navigate(`/agency-solutions?search=${encodeURIComponent(title)}`);
  };

  const knowledgeBases = [
    {
      title: "Zoning & Code Compliance: Automated Checks",
      description: "Automated systems for checking zoning compliance and building codes in federal construction projects",
      type: "Automation",
      lastUpdated: "2024-01-22"
    },
    {
      title: "Space Utilization & Circulation Analysis",
      description: "Advanced analytics for optimizing space utilization and traffic flow patterns in federal buildings",
      type: "Analytics",
      lastUpdated: "2024-02-18"
    },
    {
      title: "Daylighting Analysis: Optimization Framework",
      description: "Framework for analyzing and optimizing natural lighting in federal building designs",
      type: "Framework",
      lastUpdated: "2024-01-30"
    },
    {
      title: "View Analysis: Building Orientation & Window Placement",
      description: "Tools for analyzing views and optimizing building orientation and window placement strategies",
      type: "Analysis Tool",
      lastUpdated: "2024-02-12"
    },
    {
      title: "Acoustic Analysis: HVAC & Partition Construction",
      description: "Comprehensive acoustic analysis tools for HVAC systems and partition construction in federal facilities",
      type: "Analysis Tool",
      lastUpdated: "2024-01-25"
    },
    {
      title: "Automated Plan Checking: GSA Standards Compliance",
      description: "Automated plan checking systems ensuring compliance with GSA building standards and regulations",
      type: "Compliance Tool",
      lastUpdated: "2024-02-20"
    },
    {
      title: "Clash Detection & Conflict Resolution",
      description: "Advanced clash detection and conflict resolution tools for federal building design coordination",
      type: "Detection Tool",
      lastUpdated: "2024-02-08"
    },
    {
      title: "Predictive Issue Detection: Cost Overrun Prevention",
      description: "Predictive analytics for detecting potential issues and preventing cost overruns in federal projects",
      type: "Predictive Tool",
      lastUpdated: "2024-01-28"
    },
    {
      title: "Smart Building Management: HVAC & Lighting Optimization",
      description: "Smart building management systems for optimizing HVAC and lighting performance in federal facilities",
      type: "Management System",
      lastUpdated: "2024-02-15"
    }
  ];

  return (
    <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-6">
          <Link to="/gsa-channel" className="inline-flex items-center text-primary hover:text-primary/80 mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to GSA Channel
          </Link>
          <h1 className="text-3xl font-bold mb-2">Analysis Tools</h1>
          <p className="text-muted-foreground">
            Advanced analysis tools for compliance checking, space optimization, environmental analysis, and building performance in federal facilities.
          </p>
        </div>

        <div className="grid gap-6">
          {knowledgeBases.map((kb, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-xl mb-2">{kb.title}</CardTitle>
                    <p className="text-muted-foreground">{kb.description}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                      {kb.type}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      Updated: {kb.lastUpdated}
                    </span>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3">
                  <Button variant="outline" size="sm" onClick={() => handleViewDetails(kb.title)}>
                    <FileText className="h-4 w-4 mr-2" />
                    View Details
                  </Button>
                  <Button variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
};

export default AnalysisTools;